import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

type Trade = {
    pnl: number;
    label: string;
    // other trade properties are not needed for this chart
};

type PnLChartProps = {
    trades: Trade[];
    startingBalance?: number;
    theme?: 'light' | 'dark';
    onBarClick?: (index: number) => void;
};

const PnLChart: React.FC<PnLChartProps> = ({ trades, theme = 'light', onBarClick }) => {
    const chartData = trades.map((trade) => ({
        name: trade.label,
        pnl: trade.pnl,
    }));
    const isSingleTradeDay = chartData.length === 1;
    const yValues = chartData
        .map((entry) => Number(entry.pnl))
        .filter((value) => Number.isFinite(value));
    const yDomainMin = yValues.length ? Math.min(0, ...yValues) : 0;
    const yDomainMax = yValues.length ? Math.max(0, ...yValues) : 0;
    const yTicks = React.useMemo(() => {
        if (!yValues.length) return [0];
        const span = yDomainMax - yDomainMin;
        if (span <= 0) return [0];

        const steps = 4;
        const rawTicks = Array.from({ length: steps + 1 }, (_, idx) => {
            const value = yDomainMin + (span * idx) / steps;
            return Math.round(value);
        });

        return Array.from(new Set([...rawTicks, 0])).sort((a, b) => a - b);
    }, [yDomainMax, yDomainMin, yValues.length]);

    const isDark = theme === 'dark';
    const tickColor = isDark ? '#ffffff' : '#000000';
    const tooltipBg = isDark ? '#0b1220' : '#ffffff';
    const tooltipText = isDark ? '#e5e7eb' : '#111827';
    const gridStroke = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const zeroLine = isDark ? '#9ca3af' : '#4b5563';

    const positiveColor = 'rgba(16, 185, 129, 1)';
    const negativeColor = 'rgba(239, 68, 68, 1)';

    type CustomTooltipProps = {
        active?: boolean;
        payload?: Array<{ value?: number }>;
        label?: string | number;
        isDark: boolean;
    };

    const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, isDark }) => {
        if (active && payload && payload.length) {
            const value = Number(payload[0]?.value ?? 0);
            const isPositive = value >= 0;
            const borderColor = isDark ? '#24303b' : '#e5e7eb';

            return (
                <div
                    style={{
                        backgroundColor: tooltipBg,
                        color: tooltipText,
                        borderRadius: 8,
                        border: `1px solid ${borderColor}`,
                        padding: '8px 10px',
                        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.45)' : '0 4px 16px rgba(0,0,0,0.12)',
                    }}
                >
                    {label ? (
                        <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.85 }}>{label}</div>
                    ) : null}
                    <div style={{ fontSize: 13 }}>
                        PnL: <span style={{ color: isPositive ? positiveColor : negativeColor }}>${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart
                data={chartData}
                margin={{ top: 20, right: 16, left: -4, bottom: 12 }}
                barCategoryGap={isSingleTradeDay ? '80%' : '8%'}
                barGap={isSingleTradeDay ? 0 : 1}
                className={onBarClick ? 'tp-pnlchart-clickable' : undefined}
            >
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="name" tick={{ fill: tickColor, fontSize: 12 }} interval={0} tickMargin={12} />
                <YAxis
                    width={68}
                    domain={[yDomainMin, yDomainMax]}
                    ticks={yTicks}
                    allowDecimals={false}
                    tick={{ fill: tickColor, fontSize: 12 }}
                    tickMargin={10}
                    tickFormatter={(v) => {
                        const n = Number(v);
                        const sign = n < 0 ? '-' : '';
                        return `${sign}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
                    }}
                />
                <Tooltip
                    cursor={{ fill: 'rgba(0,0,0,0)' }}
                    content={<CustomTooltip isDark={isDark} />}
                />
                <ReferenceLine y={0} stroke={zeroLine} strokeWidth={1} />
                <Bar
                    dataKey="pnl"
                    radius={[5, 5, 0, 0]}
                    barSize={isSingleTradeDay ? 72 : 68}
                    onClick={(_, index) => {
                        if (!onBarClick) return;
                        if (typeof index !== 'number') return;
                        onBarClick(index);
                    }}
                    isAnimationActive={false}
                >
                    {chartData.map((entry, idx) => (
                        <Cell
                            key={`cell-${idx}`}
                            fill={entry.pnl >= 0 ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)'}
                            stroke={entry.pnl >= 0 ? 'rgba(16, 185, 129, 1)' : 'rgba(239, 68, 68, 1)'}
                            style={onBarClick ? ({ cursor: 'pointer', outline: 'none' } as React.CSSProperties) : ({ outline: 'none' } as React.CSSProperties)}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default PnLChart;
